import { memo, useCallback } from 'react';
import { Avatar, Button, Card, Tooltip } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { ExternalLink, Trash2 } from 'lucide-react';

import { GROUP_PERMISSIONS } from '../../constants/groupPermissions';
import { getAvatarColorClass } from '../../utils/avatarColor';
import { groupAvatarText } from '../../utils/groupPaths';
import Can from '../guards/Can';

/**
 * One group of the member: avatar, name, member count, Go to group (opens the group page) and
 * Remove from group (immediate, with an Undo toast from the tab).
 *
 * @param {object} props
 * @param {{ id: string, name: string, memberCount: number }} props.group
 * @param {(groupId: string) => void} props.onOpen
 * @param {(group: object) => void} props.onRemove
 */
function MemberGroupCard({ group, onOpen, onRemove }) {
  const { t } = useTranslation('people-access');
  const handleOpen = useCallback(() => onOpen(group.id), [onOpen, group.id]);
  const handleRemove = useCallback(() => onRemove(group), [onRemove, group]);

  return (
    <Card className="flex items-center gap-3 px-4 py-3" data-testid={`member-group-${group.id}`}>
      <Avatar
        size="lg"
        initials={groupAvatarText(group.name)}
        alt={group.name}
        className={`${getAvatarColorClass(group.name)} text-xs`}
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <h3 className="truncate text-sm font-semibold text-content">{group.name}</h3>
        <span className="text-xs text-content-muted">
          {t('memberTabs.groups.members', { count: group.memberCount })}
        </span>
      </div>
      <Can permission={GROUP_PERMISSIONS.VIEW}>
        <Tooltip content={t('memberTabs.groups.goTo')}>
          <Button
            variant="ghost"
            size="sm"
            leftIcon={ExternalLink}
            aria-label={t('memberTabs.groups.goToAria', { name: group.name })}
            onClick={handleOpen}
          />
        </Tooltip>
      </Can>
      <Can permission={GROUP_PERMISSIONS.MANAGE}>
        <Tooltip content={t('memberTabs.groups.remove')}>
          <Button
            variant="ghost"
            size="sm"
            leftIcon={Trash2}
            aria-label={t('memberTabs.groups.removeAria', { name: group.name })}
            onClick={handleRemove}
          />
        </Tooltip>
      </Can>
    </Card>
  );
}

export default memo(MemberGroupCard);
